import { plainToInstance } from 'class-transformer'
import pick from 'lodash.pick'

import { CREATE_WRITER } from '@client/collections/writer/schemas/createWriter'
import { GET_WRITERS_SALES_SUMMARY } from '@client/collections/writer/schemas/getWriterSalesSummary'
import { UPDATE_WRITER } from '@client/collections/writer/schemas/updateWriter'
import { GET_WRITER_SALES_BY_PRODUCT_TYPE } from '@client/collections/writer/schemas/getWriterSalesByProductType'
import { GET_WRITERS_SALES_REPORTS } from '@client/collections/writer/schemas/getWriterSalesReports'
import { GET_WRITER_SALES_DETAIL_BY_PRODUCT_TYPE } from '@client/collections/writer/schemas/getWriterSalesDetailByProductType'
import { gqlApiInstance } from '@client/init'
import { useFileAction } from '@hooks/file/useFileAction'
import { CertificationTaxEnum } from '@interfaces/CertificationTaxEnum'
import { ResidenceEnum } from '@interfaces/ResidenceEnum'
import { WriterEnum } from '@interfaces/WriterEnum'
import { SaleSummary } from '@models/writer/SaleSummary'
import { WriterFormType } from '@models/writer/WriterFormType'
import { WriterRequestType } from '@models/writer/WriterRequestType'
import { WriterSalesType } from '@models/writer/WriterSalesType'
import { PaginatedWriterBookSaleDetail } from '@models/writer/PaginatedWriterBookSaleDetail'
import { PaginatedWriterBookSaleReport } from '@models/writer/PaginatedWriterBookSaleReport'
import { CHECK_NUMBER_HAS_BAN } from '@client/collections/writer/schemas/checkNumberHasBan'
import { AffiliationEnum } from '@interfaces/AffiliationEnum'

export enum ProductTypeSummary {
  NOVEL_CHAPTER = 'NOVEL_CHAPTER',
  MANGA_CHAPTER = 'MANGA_CHAPTER',
  NOVEL_EBOOK = 'NOVEL_EBOOK',
  MANGA_EBOOK = 'MANGA_EBOOK',
  DONATION = 'DONATION',
}

interface QuerySaleArgs {
  startDate: Date
  endDate: Date
  limitPerPage: number
  page: number
  productType: ProductTypeSummary
  userId: number
}

interface QuerySaleDetailArgs extends QuerySaleArgs {
  bookIdOrEbookId: number
}

interface QuerySaleReportArgs {
  page: number
  perpage: number
}

export function useWriterAction() {
  const fileClient = useFileAction()

  async function createWriter(
    form: WriterFormType
  ): Promise<WriterRequestType> {
    const { id, ...bankAccount } = form.bankAccount
    const createWriterInput = await transformWriterInput({
      ...form,
      bankAccount,
    })
    const res = await gqlApiInstance.request(CREATE_WRITER, {
      createWriterInput,
    })

    return plainToInstance(WriterRequestType, res.createWriter)
  }

  async function updateWriter(
    form: WriterFormType
  ): Promise<WriterRequestType> {
    const updateWriterInput = await transformWriterInput(form)
    const res = await gqlApiInstance.request(UPDATE_WRITER, {
      updateWriterInput,
    })

    return plainToInstance(WriterRequestType, res.updateWriter)
  }

  async function transformWriterInput(form: WriterFormType) {
    const isIndividual = form.type === WriterEnum.INDIVIDUAL
    const isLocal = form.residence === ResidenceEnum.LOCAL
    let companyCertificateImgPath = form.companyCertificateImgPath?.url
    let vatRegistrationCertificateImgPath =
      form.vatRegistrationCertificateImgPath?.url
    let idCardImgPath = form.idCardImgPath?.url
    let bankCoverImgPath = form.bankAccount?.bankCoverImgPath?.url

    if (form.bankAccount.bankCoverImgPath?.blob) {
      bankCoverImgPath = await fileClient.uploadFile(
        form.bankAccount.bankCoverImgPath?.blob
      )
    }

    const writerInput = {
      type: form.type,
      affiliation: form.affiliation,
      residence: form.residence,
      sendResidence: form.isSameMainAddress
        ? form.residence
        : form.sendResidence,
      isSameMainAddress: form.isSameMainAddress,
      sendSubDistrict: form.sendSubDistrict,
      certificationTax: CertificationTaxEnum.ONLINE,
      bankAccounts: [
        {
          ...form.bankAccount,
          bankCode: String(form.bankAccount.bankCode),
          bankBranchCode: String(form.bankAccount.bankBranchCode),
          bankCoverImgPath,
          bankCountry: 'TH',
        },
      ],
      consentIds: form.consents
        .filter(consent => consent.checked)
        .map(consent => consent.id),
      // individual
      idCardImgPath: '',
      fullName: '',
      idNumber: '',
      email: '',
      phoneNumber: '',
      country: '',
      address: '',
      postcode: '',
      province: '',
      district: '',
      subDistrict: '',
      // legal
      companyName: '',
      taxId: '',
      companyCertificateImgPath: '',
      vatRegistrationCertificateImgPath: '',
      coordinatorFullName: '',
      coordinatorEmail: '',
      coordinatorPhoneNumber: '',
      accountingFullName: '',
      accountingEmail: '',
      accountingPhoneNumber: '',
      certificateCountry: '',
      certificateAddress: '',
      certificatePostcode: '',
      certificateProvince: '',
      certificateDistrict: '',
      certificateSubDistrict: '',
      sendCountry: '',
      sendAddress: '',
      sendPostcode: '',
      sendProvince: '',
      sendDistrict: '',
    }

    if (isIndividual) {
      if (isLocal && form.idCardImgPath?.blob) {
        idCardImgPath = await fileClient.uploadFile(form.idCardImgPath.blob)
      }

      if (!isLocal && form.aboardIdCardImgPath?.blob) {
        idCardImgPath = await fileClient.uploadFile(
          form.aboardIdCardImgPath.blob
        )
      }

      Object.assign(
        writerInput,
        pick(form, ['fullName', 'idNumber', 'email', 'phoneNumber']),
        { idCardImgPath }
      )
    } else {
      if (form.companyCertificateImgPath?.blob) {
        companyCertificateImgPath = await fileClient.uploadFile(
          form.companyCertificateImgPath.blob
        )
      }

      if (form.vatRegistrationCertificateImgPath?.blob) {
        vatRegistrationCertificateImgPath = await fileClient.uploadFile(
          form.vatRegistrationCertificateImgPath.blob
        )
      }
      Object.assign(
        writerInput,
        pick(form, [
          'companyName',
          'taxId',
          'coordinatorFullName',
          'coordinatorEmail',
          'coordinatorPhoneNumber',
          'accountingFullName',
          'accountingEmail',
          'accountingPhoneNumber',
        ]),
        { companyCertificateImgPath, vatRegistrationCertificateImgPath }
      )
    }

    const address = transformAddress(form, isIndividual, isLocal)
    const sendAddress = transformSendAddress(form, isIndividual, isLocal)
    Object.assign(writerInput, address, sendAddress)

    return writerInput
  }

  function transformAddress(
    form: WriterFormType,
    isIndividual: boolean,
    isLocal: boolean
  ) {
    if (isIndividual) {
      if (isLocal) {
        return pick(form, [
          'address',
          'postcode',
          'province',
          'district',
          'subDistrict',
        ])
      }
      return {
        country: form.country,
        postcode: form.aboardPostcode,
        address: form.aboardAddress,
      }
    }

    if (isLocal) {
      return pick(form, [
        'certificateAddress',
        'certificatePostcode',
        'certificateProvince',
        'certificateDistrict',
        'certificateSubDistrict',
      ])
    }
    return {
      certificateCountry: form.certificateCountry,
      certificatePostcode: form.aboardCertificatePostcode,
      certificateAddress: form.aboardCertificateAddress,
    }
  }

  function transformSendAddress(
    form: WriterFormType,
    isIndividual: boolean,
    isLocal: boolean
  ) {
    if (form.isSameMainAddress) {
      if (isLocal) {
        return {
          sendAddress: isIndividual ? form.address : form.certificateAddress,
          sendPostcode: isIndividual ? form.postcode : form.certificatePostcode,
          sendProvince: isIndividual ? form.province : form.certificateProvince,
          sendDistrict: isIndividual ? form.district : form.certificateDistrict,
          sendSubDistrict: isIndividual
            ? form.subDistrict
            : form.certificateSubDistrict,
        }
      }
      return {
        sendCountry: isIndividual ? form.country : form.certificateCountry,
        sendAddress: isIndividual
          ? form.aboardAddress
          : form.aboardCertificateAddress,
        sendPostcode: isIndividual
          ? form.aboardPostcode
          : form.aboardCertificatePostcode,
      }
    }

    if (form.sendResidence === ResidenceEnum.LOCAL) {
      return pick(form, [
        'sendAddress',
        'sendPostcode',
        'sendProvince',
        'sendDistrict',
        'sendSubDistrict',
      ])
    }
    return {
      sendCountry: form.sendCountry,
      sendAddress: form.aboardSendAddress,
      sendPostcode: form.aboardSendPostcode,
    }
  }

  async function updateWriterConsent(consentId: number) {
    await gqlApiInstance.request(UPDATE_WRITER, {
      updateWriterInput: {
        consentIds: [consentId],
        onlyConsent: true,
      },
    })
  }

  async function querySaleSummary({
    startDate,
    endDate,
  }: {
    startDate: Date
    endDate: Date
  }) {
    const { getWriterSalesSummary } = await gqlApiInstance.request(
      GET_WRITERS_SALES_SUMMARY,
      {
        getWriterSalesSummaryInput: {
          startDate,
          endDate,
        },
      }
    )
    return plainToInstance(SaleSummary, getWriterSalesSummary)
  }

  async function getWriterSalesByProductType(
    getWriterSalesByProductTypeInput: QuerySaleArgs
  ): Promise<WriterSalesType> {
    const response = await gqlApiInstance.request(
      GET_WRITER_SALES_BY_PRODUCT_TYPE,
      {
        getWriterSalesByProductTypeInput,
      }
    )

    return plainToInstance(
      WriterSalesType,
      response.getWriterSalesByProductType
    )
  }

  async function getWriterSalesDetailByProductType(
    getWriterSalesDetailByProductTypeInput: QuerySaleDetailArgs
  ): Promise<PaginatedWriterBookSaleDetail> {
    const response = await gqlApiInstance.request(
      GET_WRITER_SALES_DETAIL_BY_PRODUCT_TYPE,
      {
        getWriterSalesDetailByProductTypeInput,
      }
    )

    return plainToInstance(
      PaginatedWriterBookSaleDetail,
      response.getWriterSalesDetailByProductType.salesDetail
    )
  }

  async function querySaleReports({
    page,
    perpage,
  }: QuerySaleReportArgs): Promise<PaginatedWriterBookSaleReport> {
    const response = await gqlApiInstance.request(GET_WRITERS_SALES_REPORTS, {
      page,
      limitPerPage: perpage,
    })

    return plainToInstance(
      PaginatedWriterBookSaleReport,
      response.getWriterSalesReports
    )
  }

  async function verifyNumberHasBan(verifyId: string): Promise<boolean> {
    const { checkNumberHasBan } = await gqlApiInstance.request(
      CHECK_NUMBER_HAS_BAN,
      {
        number: verifyId,
      }
    )
    return checkNumberHasBan
  }

  return {
    createWriter,
    updateWriter,
    transformWriterInput,
    updateWriterConsent,
    querySaleSummary,
    querySaleReports,
    getWriterSalesByProductType,
    getWriterSalesDetailByProductType,
    verifyNumberHasBan,
  }
}
